import {
  Box,
  Modal,
  Typography,
  FormControl,
  Button,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  FormHelperText,
  InputLabel
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { makeStyles } from '@mui/styles';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GetRoleList } from '../../../network/Role';
import { AddUserData } from '../../../network/User';
import { useAuth } from '../../customContextProviders/AuthContext';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '30%',
  maxWidth: '35%',
  bgcolor: 'background.paper',
  p: 4,
  borderRadius: 1,
  maxHeight: '90vh',
  overflowY: 'auto'
};

const useStyles = makeStyles((theme) => ({
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  }
}));

const addAdminSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  address: Yup.string().required('Address is required'),
  phone: Yup.string().required('Phone number is required'),
  password: Yup.string().required('Password is required'),
  roleId: Yup.string().required('Role is required')
});

export default function AddNewAdmin({
  openAssignedModal,
  handleClose,
  orgData,
  setAlertMetas,
  alertMetas
}) {
  const classes = useStyles();
  const { sessionUserData } = useAuth();
  const orgId = sessionUserData?.user?.organization?.id;
  const userRole = sessionUserData?.user?.userRole?.roleName;
  const [showPassword, setShowPassword] = useState(false);
  const [roleList, setRoleList] = useState([]);

  const addAdminFormik = useFormik({
    initialValues: {
      name: '',
      email: '',
      address: '',
      phone: '',
      password: '',
      roleId: '',
      role: userRole.toUpperCase() === 'SUPERADMIN' ? 'admin' : 'user'
    },
    validationSchema: addAdminSchema,
    onSubmit: (values, { resetForm }) => {
      addAdminFormik.isSubmitting = true;
      const payload = {
        email: values.email,
        name: values.name,
        phone: values.phone,
        role: values.role,
        roleId: values.roleId,
        password: values.password,
        organizationId: orgData?.id
      };
      AddNewAdmin(payload);
      resetForm();
    }
  });

  const { errors, touched, values, handleSubmit, getFieldProps, setFieldValue } = addAdminFormik;

  const getAllRole = async () => {
    try {
      const res = await GetRoleList(orgId);
      if (res) {
        setRoleList(res?.data?.data);
      }
    } catch (error) {
      console.log(error.response.data.message);
    }
  };

  const AddNewAdmin = async (payload) => {
    try {
      const res = await AddUserData(payload);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'success',
          snackBarOpened: true,
          msg: 'Admin Created Successfully !'
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
        handleClose();
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
      handleClose();
    }
  };

  useEffect(() => {
    getAllRole();
  }, []);

  useEffect(() => {
    if (orgData.id) {
      addAdminFormik.setFieldValue('name', orgData.name);
      addAdminFormik.setFieldValue('email', orgData.email);
      addAdminFormik.setFieldValue('phone', orgData.phone);
      addAdminFormik.setFieldValue('address', orgData.address);
    }
  }, [orgData]);

  return (
    <Box>
      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="create-admin-modal"
      >
        <Box sx={style}>
          <Typography sx={{ fontSize: 18, fontWeight: 900 }}>Create New Admin</Typography>

          <FormikProvider value={addAdminFormik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Box>
                <FormControl fullWidth sx={{ mt: 3 }}>
                  <TextField
                    label="Name"
                    id="name"
                    name="name"
                    variant="outlined"
                    {...getFieldProps('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </FormControl>
                <FormControl fullWidth sx={{ mt: 3 }}>
                  <TextField
                    label="Email"
                    id="email"
                    name="email"
                    variant="outlined"
                    {...getFieldProps('email')}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </FormControl>
                <FormControl fullWidth sx={{ mt: 3 }}>
                  <TextField
                    label="Mobile Number"
                    id="phone"
                    name="phone"
                    variant="outlined"
                    {...getFieldProps('phone')}
                    error={Boolean(touched.phone && errors.phone)}
                    helperText={touched.phone && errors.phone}
                  />
                </FormControl>
                <FormControl fullWidth sx={{ mt: 3 }}>
                  <TextField
                    label="Address"
                    id="address"
                    name="address"
                    variant="outlined"
                    {...getFieldProps('address')}
                    error={Boolean(touched.address && errors.address)}
                    helperText={touched.address && errors.address}
                  />
                </FormControl>{' '}
                <FormControl data-testid="password" fullWidth sx={{ mt: 3 }}>
                  <TextField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    variant="outlined"
                    {...getFieldProps('password')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                  />
                </FormControl>
                <FormControl
                  fullWidth
                  error={Boolean(touched.roleId && errors.roleId)}
                  sx={{ mt: 3 }}
                >
                  <InputLabel
                    sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: '500' }}
                    id="roleId-label"
                  >
                    Select Role *
                  </InputLabel>
                  <Select
                    sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: '500' }}
                    labelId="roleId-label"
                    label="Select Role *"
                    IconComponent={ExpandMoreIcon}
                    id="roleId"
                    name="roleId"
                    {...getFieldProps('roleId')}
                    onChange={(e) => {
                      const selectedRole = roleList.find((role) => role.id === e.target.value);
                      setFieldValue('roleId', selectedRole?.id);
                      // setFieldValue('role', selectedRole?.name);
                    }}
                  >
                    {roleList.map((item) => (
                      <MenuItem className={classes.menuItem} value={item.id} key={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched?.roleId && errors?.roleId && (
                    <FormHelperText>{errors?.roleId}</FormHelperText>
                  )}
                </FormControl>
                <Box sx={{ display: 'flex', float: 'right', flexWrap: 'wrap', mt: 3 }}>
                  <Button variant="contained" sx={{ mr: 2 }} onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button variant="contained" type="submit">
                    Add
                  </Button>
                </Box>
              </Box>
            </Form>
          </FormikProvider>
        </Box>
      </Modal>
    </Box>
  );
}
